import { useState } from 'react';
import { useParams } from 'react-router-dom';
import activityStyles from '../_shared/ActivityCard.module.scss';
import ActivityCountPill from '../_shared/ActivityCountPill';
import ReconciliationsDrawer from './ReconciliationsDrawer';
import DegradedIndicator from './components/DegradedIndicator';
import { useReconciliationsLogic } from './hooks/useReconciliationsLogic';
import { RECON_SCOPE } from '../../../../store/reconciliations/constants';

/** Sidebar chip. Cache release-on-org-change is owned by the page container
 *  (useReleaseReconciliationsOnOrgChange in Dashboard/index.tsx), not here — this chip is
 *  itself conditionally rendered (mobile-hidden, visibility-flag-gated). */
function ReconciliationsChip() {
    const params = useParams();
    const orgId = params?.id;
    const [showDrawer, setShowDrawer] = useState(false);
    const { entry } = useReconciliationsLogic(orgId);

    const isLoading = !entry || entry.status === 'loading';
    // Never a 0% for a failure (A-78) — an error, or a payload with no overall score
    // (zero-audited org, A-87), renders disabled and blank, exactly like AlertEngine's zero state.
    // The whole `overall` block is nullable on the wire and the saga only fails a payload that has
    // neither it nor any section, so the absent case has to be tested before the null-score one:
    // `overall?.score === null` is false when `overall` itself is null.
    const overall = entry?.payload?.overall;
    const isUnavailable = entry?.status === 'error' || (entry?.status === 'success' && (!overall || overall.score === null));
    // Evidence loss, not an ordinary data gap (D27a) — never folded into isUnavailable/disabled,
    // which stays reserved for the A-78 failure state (A-125c).
    const degraded = entry?.payload?.degraded;
    const isDegraded = !!degraded?.isDegraded;
    // The badge is always red (ticket: "lowest % reconciled, red indicator") — except degraded
    // (D29): a data-failure state outranks it. `muted` outranks both — ActivityCountPill ignores
    // `tone` while muted.
    const tone = isDegraded ? 'warning' : 'alerts';

    return (
        <>
            <button
                type="button"
                className={activityStyles.SidebarChip}
                onClick={() => setShowDrawer(true)}
                disabled={isLoading || isUnavailable}
            >
                <span className={activityStyles.SidebarLabel}>Reconciliations</span>
                {isDegraded && degraded && <DegradedIndicator degraded={degraded} />}
                <ActivityCountPill
                    tone={tone}
                    count={isLoading ? '' : isUnavailable ? '' : `${overall?.score}%`}
                    muted={isLoading || isUnavailable}
                    compactArrow
                />
            </button>
            {showDrawer && orgId && (
                <ReconciliationsDrawer orgId={orgId} scope={RECON_SCOPE.ALL} onClose={() => setShowDrawer(false)} />
            )}
        </>
    );
}

export default ReconciliationsChip;
